import { useState, useEffect, useMemo } from 'react';
import { api } from '../services/api';

// Normalized topic shape the modal works with internally
export interface PickerTopic {
  id: number;
  title: string;
  primary_keyword: string | null;
  generator_type: string;
  priority_score: number | null;
}

interface TopicPickerModalProps {
  projectId: string | number;
  isOpen: boolean;
  onClose: () => void;
  // Now returns topics paired with their computed scheduled dates
  onSelect: (items: Array<{ topic: PickerTopic; scheduled_date: string }>) => void;
  // Optional: pre-loaded topics from the parent (e.g. SOP topical map).
  // When provided, the modal skips its own fetch and uses these directly.
  preloadedTopics?: PickerTopic[];
}

// Distribute N items across start..end (inclusive) dates, skipping weekends.
function distributeDates(count: number, startISO: string, endISO: string, skipWeekends = true): string[] {
  if (count <= 0) return [];
  const start = new Date(startISO + 'T00:00:00');
  const end = new Date(endISO + 'T00:00:00');
  if (isNaN(start.getTime()) || isNaN(end.getTime()) || end < start) {
    return new Array(count).fill(startISO);
  }
  const days: string[] = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    const dow = cursor.getDay();
    if (!skipWeekends || (dow !== 0 && dow !== 6)) {
      days.push(cursor.toISOString().split('T')[0]);
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  const pool = days.length > 0 ? days : (() => {
    const all: string[] = [];
    const c2 = new Date(start);
    while (c2 <= end) {
      all.push(c2.toISOString().split('T')[0]);
      c2.setDate(c2.getDate() + 1);
    }
    return all.length > 0 ? all : [startISO];
  })();

  if (count <= pool.length) {
    const out: string[] = [];
    for (let i = 0; i < count; i++) {
      const idx = Math.round((i * (pool.length - 1)) / Math.max(1, count - 1));
      out.push(pool[Math.min(idx, pool.length - 1)]);
    }
    return out;
  }
  const out: string[] = [];
  for (let i = 0; i < count; i++) {
    out.push(pool[i % pool.length]);
  }
  return out;
}

export default function TopicPickerModal({ projectId, isOpen, onClose, onSelect, preloadedTopics }: TopicPickerModalProps) {
  const [jobs, setJobs] = useState<Array<{ id: number; created_at: string }>>([]);
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);
  const [topics, setTopics] = useState<PickerTopic[]>([]);
  const [selectedTopics, setSelectedTopics] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const today = new Date().toISOString().split('T')[0];
  const defaultEnd = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 14);
    return d.toISOString().split('T')[0];
  })();
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [skipWeekends, setSkipWeekends] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // When opening: use preloaded topics if provided, else fetch from legacy topicalmap endpoint
  useEffect(() => {
    if (!isOpen) return;
    if (preloadedTopics && preloadedTopics.length >= 0) {
      setTopics(preloadedTopics);
      setJobs([]);
      setSelectedJobId(null);
      return;
    }
    if (projectId) {
      loadJobs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, projectId, preloadedTopics]);

  const loadJobs = async () => {
    try {
      const response = await api.get(`/topicalmap/jobs/${projectId}`);
      const jobsList = response.data.jobs || [];
      setJobs(jobsList);
      if (jobsList.length > 0) {
        setSelectedJobId(jobsList[0].id);
        loadTopics(jobsList[0].id);
      }
    } catch (error) {
      console.error('Failed to load topical map jobs:', error);
    }
  };

  const loadTopics = async (jobId: number) => {
    setLoading(true);
    try {
      const response = await api.get(`/topicalmap/results/${jobId}`);
      const topicsList = response.data.pages || [];
      setTopics(topicsList);
    } catch (error) {
      console.error('Failed to load topics:', error);
    }
    setLoading(false);
  };

  const toggleTopic = (topicId: number) => {
    const newSelected = new Set(selectedTopics);
    if (newSelected.has(topicId)) {
      newSelected.delete(topicId);
    } else {
      newSelected.add(topicId);
    }
    setSelectedTopics(newSelected);
  };

  // Filter topics by search (title, keyword, generator type)
  const filteredTopics = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return topics;
    return topics.filter(t =>
      (t.title || '').toLowerCase().includes(q) ||
      (t.primary_keyword || '').toLowerCase().includes(q) ||
      (t.generator_type || '').toLowerCase().includes(q)
    );
  }, [topics, searchQuery]);

  const selectAll = () => {
    // Select all currently visible (filtered) topics, preserving other selections
    setSelectedTopics(prev => {
      const n = new Set(prev);
      filteredTopics.forEach(t => n.add(t.id));
      return n;
    });
  };

  const deselectAll = () => {
    // Clear selections among currently visible (filtered) topics only
    setSelectedTopics(prev => {
      const n = new Set(prev);
      filteredTopics.forEach(t => n.delete(t.id));
      return n;
    });
  };

  const handleAdd = () => {
    const selectedList = topics.filter(t => selectedTopics.has(t.id));
    if (selectedList.length === 0) return;
    const dates = distributeDates(selectedList.length, startDate, endDate, skipWeekends);
    const items = selectedList.map((topic, i) => ({
      topic,
      scheduled_date: dates[i] || startDate,
    }));
    onSelect(items);
    handleClose();
  };

  const handleClose = () => {
    setSelectedTopics(new Set());
    setSearchQuery('');
    onClose();
  };

  if (!isOpen) return null;

  const dateRangeInvalid = !startDate || !endDate || new Date(endDate) < new Date(startDate);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Add Topics from Topical Map</h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Job Selector (hidden when using preloadedTopics) */}
          {!preloadedTopics && jobs.length > 0 && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Topical Map</label>
              <select
                value={selectedJobId || ''}
                onChange={(e) => {
                  const jobId = parseInt(e.target.value);
                  setSelectedJobId(jobId);
                  loadTopics(jobId);
                }}
                className="input w-full"
              >
                {jobs.map(job => (
                  <option key={job.id} value={job.id}>
                    Topical Map - {new Date(job.created_at).toLocaleDateString()}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Search */}
          <div className="mb-3">
            <div className="relative">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
              </svg>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by title, keyword, or category..."
                className="w-full pl-9 pr-9 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  title="Clear search"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Multi-select controls */}
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="text-sm text-gray-600">
              {searchQuery ? `${filteredTopics.length} of ${topics.length} topics` : `${topics.length} topics`}
              {selectedTopics.size > 0 ? ` · ${selectedTopics.size} selected` : ''}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={selectAll} className="btn btn-secondary btn-sm">
                {searchQuery ? 'Select Visible' : 'Select All'}
              </button>
              <button onClick={deselectAll} className="btn btn-secondary btn-sm">Clear</button>
            </div>
          </div>

          {/* Topics List */}
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
              <p className="mt-2 text-gray-600">Loading topics...</p>
            </div>
          ) : topics.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No topics found in this topical map
            </div>
          ) : filteredTopics.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No topics match "{searchQuery}"
            </div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto border border-gray-200 rounded-lg p-2 bg-gray-50">
              {filteredTopics.map(topic => {
                const priority = (topic.priority_score || 0) >= 70 ? 'High' : (topic.priority_score || 0) >= 40 ? 'Medium' : 'Low';
                const priorityColor = (topic.priority_score || 0) >= 70 ? 'text-green-600' : (topic.priority_score || 0) >= 40 ? 'text-yellow-600' : 'text-gray-500';

                return (
                  <label
                    key={topic.id}
                    className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors bg-white ${
                      selectedTopics.has(topic.id)
                        ? 'border-brand-500 bg-brand-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedTopics.has(topic.id)}
                      onChange={() => toggleTopic(topic.id)}
                      className="mt-1 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 text-sm">{topic.title}</p>
                      {topic.primary_keyword && (
                        <p className="text-xs text-gray-600 mt-1">Keyword: {topic.primary_keyword}</p>
                      )}
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                          {topic.generator_type?.replace(/_/g, ' ')}
                        </span>
                        <span className={`text-xs font-medium ${priorityColor}`}>
                          {priority}
                        </span>
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="input w-40"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="input w-40"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700 mb-1.5">
                <input
                  type="checkbox"
                  checked={skipWeekends}
                  onChange={(e) => setSkipWeekends(e.target.checked)}
                  className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                />
                Skip weekends
              </label>
            </div>
            <div className="flex gap-3">
              <button onClick={handleClose} className="btn btn-secondary">
                Cancel
              </button>
              <button
                onClick={handleAdd}
                disabled={selectedTopics.size === 0 || dateRangeInvalid}
                className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                title={dateRangeInvalid ? 'End date must be on or after start date' : ''}
              >
                Add {selectedTopics.size > 0 && `(${selectedTopics.size})`} to Calendar
              </button>
            </div>
          </div>
          {dateRangeInvalid && (
            <p className="mt-2 text-xs text-red-600">End date must be on or after start date.</p>
          )}
        </div>
      </div>
    </div>
  );
}
